import React, { useState, useMemo, useEffect, useRef } from "react";
import { Plus, X, Search, Zap, ChevronDown, ArrowLeft } from "lucide-react";
import axios from "axios";
import { ALL_COMMANDS } from "./CommandPalette";
import { canUseCommand } from "../shared/permissions";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RACCOURCIS = 6;

const parseAction = (cmd) => {
  if (!cmd || !cmd.action) return null;
  if (cmd.action.startsWith("tab:")) {
    return { type: "tab", tab: cmd.action.split(":")[1] };
  }
  if (cmd.action.startsWith("subtab:")) {
    const [, parentTab, subTab] = cmd.action.split(":");
    return { type: "subtab", parentTab, subTab };
  }
  return null;
};

const groupBy = (cmds) => Object.entries(cmds.reduce((acc, c) => {
  const g = c.group || "Autre";
  (acc[g] = acc[g] || []).push(c);
  return acc;
}, {}));

const ListeAjout = ({ available, onAdd }) => (
  <div className="max-h-72 overflow-y-auto">
    {available.length === 0 ? (
      <div className="px-3 py-6 text-center text-xs text-gray-400">Aucune rubrique</div>
    ) : groupBy(available).map(([group, cmds]) => (
      <div key={group}>
        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50 sticky top-0">{group}</div>
        {cmds.map((cmd) => {
          const Icon = cmd.icon;
          return (
            <button key={cmd.id} type="button" onClick={() => onAdd(cmd)} data-testid={`raccourci-add-${cmd.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-[#EAF2FC]/60">
              {Icon && <Icon className="h-4 w-4 text-[#1173D4]" />}
              <span className="flex-1 truncate">{cmd.label}</span>
              <Plus className="h-3.5 w-3.5 text-gray-400" />
            </button>
          );
        })}
      </div>
    ))}
  </div>
);

const Raccourcis = ({ raccourcis = [], user, onAction, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  const NAVIGABLE = useMemo(
    () => ALL_COMMANDS
      .filter((c) => c.action?.startsWith("tab:") || c.action?.startsWith("subtab:"))
      .filter((c) => canUseCommand(user, c)),
    [user]
  );

  const shortcuts = useMemo(
    () => raccourcis.map((id) => NAVIGABLE.find((c) => c.id === id)).filter(Boolean),
    [raccourcis, NAVIGABLE]
  );

  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return NAVIGABLE.filter((c) => !raccourcis.includes(c.id))
      .filter((c) => !q || (c.label + " " + (c.keywords || "") + " " + (c.group || "")).toLowerCase().includes(q))
      .sort((a, b) => (a.group || "").localeCompare(b.group || "") || a.label.localeCompare(b.label));
  }, [NAVIGABLE, raccourcis, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const saveIds = async (nextIds) => {
    onUpdate?.(nextIds);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${BACKEND_URL}/api/users/me/raccourcis`,
        { raccourcis: nextIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      toast.error("Impossible d'enregistrer les raccourcis");
    }
  };

  const addShortcut = (cmd) => {
    if (raccourcis.length >= MAX_RACCOURCIS) {
      toast.error(`Maximum ${MAX_RACCOURCIS} raccourcis`);
      return;
    }
    if (raccourcis.includes(cmd.id)) return;
    saveIds([...raccourcis, cmd.id]);
    setQuery("");
    setAdding(false);
  };

  const removeShortcut = (id) => saveIds(raccourcis.filter((x) => x !== id));

  const handleClick = (cmd) => {
    const act = parseAction(cmd);
    if (act) onAction?.(act);
    setOpen(false);
  };

  return (
    <div className="relative hidden lg:block" ref={ref} data-testid="raccourcis-bar">
      <button type="button" onClick={() => { setOpen((o) => !o); setAdding(false); }} data-testid="raccourcis-toggle"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#1173D4]/30 bg-[#EAF2FC]/50 text-sm font-medium text-[#0B1F3A] hover:bg-[#EAF2FC] transition-colors">
        <Zap className="h-4 w-4 text-[#1173D4]" />
        Raccourcis
        <ChevronDown className={`h-3.5 w-3.5 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="raccourcis-menu">
          {adding ? (
            <>
              <div className="p-2 border-b border-gray-100 flex items-center gap-2">
                <button type="button" onClick={() => setAdding(false)} className="p-1 rounded hover:bg-gray-100 text-gray-500" title="Retour" data-testid="raccourci-picker-back"><ArrowLeft className="h-4 w-4" /></button>
                <Search className="h-4 w-4 text-gray-400" />
                <input autoFocus type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une rubrique…"
                  data-testid="raccourci-search-input" className="flex-1 h-8 text-sm border-0 outline-none focus:ring-0 bg-transparent" />
              </div>
              <ListeAjout available={available} onAdd={addShortcut} />
            </>
          ) : (
            <>
              {shortcuts.length === 0 ? (
                <div className="px-3 py-5 text-center text-xs text-gray-400" data-testid="raccourcis-empty">Aucun raccourci pour l'instant</div>
              ) : (
                <div className="py-1">
                  {shortcuts.map((cmd) => {
                    const Icon = cmd.icon;
                    return (
                      <div key={cmd.id} className="group flex items-center hover:bg-[#EAF2FC]/60">
                        <button type="button" onClick={() => handleClick(cmd)} data-testid={`raccourci-${cmd.id}`}
                          className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:text-[#1173D4]">
                          {Icon && <Icon className="h-4 w-4 text-[#1173D4]" />}
                          <span className="truncate">{cmd.label}</span>
                        </button>
                        <button type="button" onClick={() => removeShortcut(cmd.id)} data-testid={`raccourci-remove-${cmd.id}`} title="Retirer"
                          className="mr-2 p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50">
                <span className="text-[11px] text-gray-500">{raccourcis.length}/{MAX_RACCOURCIS} raccourcis</span>
                {raccourcis.length < MAX_RACCOURCIS && (
                  <button type="button" onClick={() => setAdding(true)} data-testid="raccourci-add-btn"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1173D4] hover:underline">
                    <Plus className="h-3.5 w-3.5" /> Ajouter
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Raccourcis;
